<template>
    <div class="widget-table-columns-alignment-field">
        <div class="w-full mb-3">
            <RedesignFormLabel
                :label="t('widget.tableColumnsAlignment')"
                class="mb-2"
            />
            <VcRadioButtonsGroup
                v-model="displayedAlignment"
                :radios="alignmentOptions"
                :config="{
                    valueKey: 'value',
                    labelKey: 'label',
                    iconKey: 'icon'
                }"
                class="alignment-buttons-group"
            >
                <template #item="{ option }">
                    <RedesignBaseIcon
                        :name="option.originalItem.icon"
                        :size="24"
                    />
                </template>
            </VcRadioButtonsGroup>
        </div>
    </div>
</template>

<script setup lang="ts">
import { VcRadioButtonsGroup } from '@voicenter-team/voicenter-ui-plus'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { computed } from 'vue'
import { isRTL } from '@/composables/useDirection'
import type { TableAlignmentType } from '@/types/alignment'
import { TableAlignment } from '@/types/alignment'
import { invertAlignmentForRTL } from '@/helpers/alignmentUtils'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const alignmentOptions = [
    {
        value: 'left',
        label: 'left',
        icon: 'AlignLeft'
    },
    {
        value: 'center',
        label: 'center',
        icon: 'AlignCenter'
    },
    {
        value: 'right',
        label: 'right',
        icon: 'AlignRight'
    }
]

const props = withDefaults(defineProps<{
    alignment?: TableAlignmentType
    hasColumnSpecificAlignment?: boolean
}>(), {
    hasColumnSpecificAlignment: false
})

const emit = defineEmits<{
    (e: 'update:alignment', value: TableAlignmentType): void
}>()

const displayedAlignment = computed<TableAlignmentType | undefined>({
    get () {
        if (props.hasColumnSpecificAlignment) {
            return undefined
        }

        if (props.alignment === undefined) {
            return isRTL.value ? TableAlignment.right : TableAlignment.left
        }

        return invertAlignmentForRTL(props.alignment)
    },
    set (val: TableAlignmentType | undefined) {
        if (val === undefined) return
        emit('update:alignment', invertAlignmentForRTL(val))
    }
})
</script>

<style lang="scss" scoped>
.alignment-buttons-group {
    direction: ltr !important;
    display: inline-flex !important;
    flex-flow: row nowrap !important;
    gap: 0 !important;
}
</style>
